import type { UserType } from '@metadevprofile/shared';
import { Avatar, buttonVariants, Dropdown } from '@metadevprofile/ui/web';
import { LogOutIcon, User2Icon } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { authRoutes as authRoutePaths } from '@/auth/constants/routes';
import { resetAuth, selectUser } from '@/auth/stores/auth-slice';
import { signOut } from '@/auth/stores/usecases/sign-out';
import { getProfileRoute } from '@/common/lib/get-route-by-user-type';
import { resetFeatureStores } from '@/common/lib/reset-feature-stores';
import { useAppDispatch, useAppSelector } from '@/common/redux/store';
import { useInstitution } from '@/institution/hooks/use-institution';
import { useProfile } from '@/profile/hooks/use-profile';

export const Footer = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const { profile } = useProfile({ skip: !user });
  const { institution } = useInstitution({ skip: user?.type !== 'INSTITUTION' });

  const { t } = useTranslation('common');

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } finally {
      dispatch(resetAuth());
      await resetFeatureStores();
      navigate(authRoutePaths.signIn);
    }
  }, [dispatch, navigate]);

  const getInitials = useCallback((name: string | undefined): string => {
    // A name can carry empty parts — a missing last name renders as `"John "`, and double or
    // leading spaces split into `''` too. Drop the blanks before indexing instead of trusting
    // the positions returned by split().
    const parts = name?.split(' ').filter(Boolean) ?? [];

    if (parts.length === 0) return 'U';

    if (parts.length === 1) return parts[0][0].toUpperCase();

    return parts[0][0].toUpperCase() + parts[1][0].toUpperCase();
  }, []);

  const getDisplayRole = useCallback(
    (role: UserType): string => {
      switch (role) {
        case 'PRACTITIONER':
          return t('roles.practitioner');
        case 'INSTITUTION':
          return t('roles.institution');
        case 'ADMIN':
          return t('roles.admin');
        default:
          return t('roles.unknown');
      }
    },
    [t],
  );

  if (!profile || !user) return null;

  const isInstitution = user.type === 'INSTITUTION';
  const avatarSrc = isInstitution ? institution?.avatar_url || '' : profile?.avatar_url || '';
  const initials = isInstitution
    ? getInitials(institution?.display_name || '').toUpperCase()
    : getInitials(`${profile.first_name} ${profile.last_name}`).toUpperCase();

  return (
    <div className="flex items-center gap-3 justify-between w-full">
      <Dropdown>
        <Dropdown.Trigger className="flex items-center gap-3 rounded-md">
          <div className="relative">
            <Avatar className="size-10 rounded-md border-2 border-border">
              <Avatar.Image src={avatarSrc} />
              <Avatar.Fallback className="text-muted bg-surface-secondary rounded-md">{initials}</Avatar.Fallback>
            </Avatar>
            {isInstitution && profile.avatar_url ? (
              <div className="absolute -bottom-1 -right-1 size-5 rounded-full overflow-hidden">
                <img src={profile.avatar_url} alt={t('ui.avatar')} width={20} height={20} className="rounded-full" />
              </div>
            ) : null}
          </div>

          <div className="flex flex-col text-left">
            <span className="text-foreground text-sm font-semibold">{`${profile?.first_name} ${profile?.last_name}`}</span>
            <span className="text-muted text-xs">{getDisplayRole(user.type)}</span>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Popover placement="top start">
          <Dropdown.Menu onAction={handleSignOut}>
            <Dropdown.Item id="sign-out" textValue={t('signOut')} className="text-danger">
              <LogOutIcon className="mr-2 h-4 w-4 text-danger" />
              {t('signOut')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
      {user.type !== 'ADMIN' ? (
        <Link to={getProfileRoute(user)} className={buttonVariants({ variant: 'ghost', isIconOnly: true })}>
          <User2Icon className="h-5 w-5 text-muted" />
        </Link>
      ) : null}
    </div>
  );
};
